/**
 * RepeatableTextList — add/remove/edit rows of single-line strings.
 *
 * Used for `icp_segments`, `value_props`, and similar string-array
 * fields on the TenantProfile. Empty rows are allowed during editing
 * (the user can add a blank row and type into it); the parent
 * component filters them out on save if needed.
 *
 * Phase 44.4.
 */

import { useCallback } from "react";

export default function RepeatableTextList({
  label,
  items = [],
  onChange,
  placeholder = "",
  maxLength = 500,
  addLabel = "Add",
  helpText,
}) {
  const handleItemChange = useCallback(
    (index, value) => {
      const next = items.slice();
      next[index] = value;
      onChange(next);
    },
    [items, onChange]
  );

  const handleRemove = useCallback(
    (index) => {
      const next = items.slice();
      next.splice(index, 1);
      onChange(next);
    },
    [items, onChange]
  );

  const handleAdd = useCallback(() => {
    onChange([...items, ""]);
  }, [items, onChange]);

  return (
    <div className="repeatable-list">
      {label && <label className="repeatable-list__label">{label}</label>}
      {helpText && <p className="repeatable-list__help">{helpText}</p>}
      {items.length === 0 && (
        <p className="repeatable-list__empty">No items yet.</p>
      )}
      {items.map((item, i) => (
        <div key={i} className="repeatable-list__row">
          <input
            type="text"
            className="repeatable-list__input"
            value={item}
            onChange={(e) => handleItemChange(i, e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => handleRemove(i)}
            aria-label={`Remove ${label || "item"} ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-secondary btn-sm" onClick={handleAdd}>
        + {addLabel}
      </button>
    </div>
  );
}
